class ContactForm {
  private form: HTMLFormElement;
  private submitButton: HTMLButtonElement;
  private loader: HTMLElement | null = null;
  private messageBox: HTMLElement | null = null;

  constructor(formSelector: string) {
    const formElement = document.querySelector<HTMLFormElement>(formSelector);
    if (!formElement) {
      throw new Error(`Formulário não encontrado: ${formSelector}`);
    }

    this.form = formElement;
    this.submitButton = this.form.querySelector<HTMLButtonElement>(
      'button[type="submit"]'
    )!;

    if (!this.submitButton) {
      throw new Error("Botão de submit não encontrado.");
    }

    this.init();
  }

  private init(): void {
    this.form.addEventListener("submit", (e) => this.handleSubmit(e));
    this.createLoader();
    this.createMessageBox();
    this.setupPhoneMask();
  }

  private setupPhoneMask(): void {
    const whatsappField = this.form.querySelector<HTMLInputElement>(
      'input[name="whatsapp"]'
    );
    if (!whatsappField) return;

    whatsappField.addEventListener("input", (e) => {
      const input = e.target as HTMLInputElement;
      let value = input.value.replace(/\D/g, "");

      if (value.length > 11) value = value.slice(0, 11);

      value = value.replace(/^(\d{2})(\d)/g, "($1) $2");
      value = value.replace(/(\d)(\d{4})$/, "$1-$2");

      input.value = value;
    });
  }

  private createLoader(): void {
    this.loader = document.createElement("div");
    this.loader.className = "contact__loader";
    this.loader.innerHTML =
      '<div class="contact__loader-spinner"></div><p>Enviando...</p>';
    this.loader.style.display = "none";
    this.form.appendChild(this.loader);
  }

  private createMessageBox(): void {
    this.messageBox = document.createElement("div");
    this.messageBox.className = "contact__message";
    this.messageBox.style.display = "none";
    this.form.appendChild(this.messageBox);
  }

  private showLoader(): void {
    if (this.loader) {
      this.loader.style.display = "flex";
      this.toggleSubmitState(true);
    }
  }

  private hideLoader(): void {
    if (this.loader) {
      this.loader.style.display = "none";
      this.toggleSubmitState(false);
    }
  }

  private toggleSubmitState(disabled: boolean): void {
    if (this.submitButton) {
      this.submitButton.disabled = disabled;
      this.submitButton.style.opacity = disabled ? "0.6" : "1";
    }
  }

  private showMessage(type: "success" | "error", message: string): void {
    if (!this.messageBox) return;

    this.messageBox.className = `contact__message contact__message--${type}`;
    this.messageBox.textContent = message;
    this.messageBox.style.display = "block";

    setTimeout(() => {
      if (this.messageBox) this.messageBox.style.display = "none";
    }, 5000);
  }

  private validateForm(): boolean {
    this.clearErrors();
    let isValid = true;

    const fields = this.form.querySelectorAll<
      HTMLInputElement | HTMLSelectElement
    >(".contact__input, .contact__select");

    fields.forEach((field) => {
      const error = this.validateField(field);
      if (error) {
        this.showError(field, error);
        isValid = false;
      }
    });

    return isValid;
  }

  private validateField(
    field: HTMLInputElement | HTMLSelectElement
  ): string | null {
    const value = field.value.trim();
    const name = field.name;
    const isRequired = field.required || field.dataset.required === "true";

    if (isRequired && !value) {
      if (
        field.tagName === "SELECT" &&
        (field as HTMLSelectElement).value === ""
      ) {
        return `${this.getFieldLabel(name)} é obrigatório`;
      }
      return `${this.getFieldLabel(name)} é obrigatório`;
    }

    if (value) {
      if (name === "nome") {
        const nameParts = value.split(/\s+/);
        if (nameParts.length < 2) {
          return "Por favor, digite seu nome e sobrenome";
        }
        if (nameParts[1].length < 2) {
          return "Sobrenome muito curto";
        }
      }

      if (field.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return "E-mail inválido";
      }

      if (name === "whatsapp" && value.replace(/\D/g, "").length < 10) {
        return "WhatsApp incompleto";
      }
    }

    return null;
  }

  private getFieldLabel(fieldName: string): string {
    const labels: Record<string, string> = {
      nome: "Nome completo",
      email: "E-mail",
      whatsapp: "WhatsApp",
      importa: "Já importa?",
      interesse: "Interesse",
      empresa: "Empresa / Cargo",
    };
    return labels[fieldName] || fieldName;
  }

  private clearErrors(): void {
    this.form.querySelectorAll(".contact__error").forEach((el) => el.remove());
    this.form
      .querySelectorAll(".contact__input--error")
      .forEach((el) => el.classList.remove("contact__input--error"));
  }

  private showError(field: Element, message: string): void {
    field.classList.add("contact__input--error");

    const errorElement = document.createElement("span");
    errorElement.className = "contact__error";
    errorElement.textContent = message;

    field.closest(".contact__form-field")?.appendChild(errorElement);
  }

  private getUtmParameters(): Record<string, string> {
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams: Record<string, string> = {};

    utmParams.utm_source =
      urlParams.get("utm_source") || document.referrer || "Desconhecido";
    utmParams.utm_medium = urlParams.get("utm_medium") || "Desconhecido";
    utmParams.utm_campaign = urlParams.get("utm_campaign") || "Desconhecido";
    utmParams.utm_content = urlParams.get("utm_content") || "Desconhecido";
    utmParams.utm_term = urlParams.get("utm_term") || "";
    utmParams.utm_referrer = document.referrer || "";

    return utmParams;
  }

  private getFormData(): Record<string, string> {
    const formDataObj: Record<string, string> = {};
    const formData = new FormData(this.form);

    formData.forEach((value, key) => {
      formDataObj[key] = value.toString().trim();
    });

    return formDataObj;
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    if (!this.validateForm()) return;

    this.showLoader();

    const formData = this.getFormData();
    const utmParams = this.getUtmParameters();

    const payload = {
      name: formData.nome,
      email: formData.email,
      phone: formData.whatsapp.replace(/\D/g, ""),
      importa: formData.importa || "",
      interesse: formData.interesse || "",
      empresa: formData.empresa || "",
      ...utmParams,
    };

    await fetch("php/pixel-submit.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    try {
      const response = await fetch("php/submit.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => null);

      if (response.ok && (result?.success || result?.status === "success")) {
        window.location.href = "/obrigado.html";
      } else {
        this.showMessage(
          "error",
          result?.message || "Erro ao enviar mensagem."
        );
      }
    } catch (error) {
      this.showMessage("error", "Erro de conexão. Tente novamente mais tarde.");
    } finally {
      this.hideLoader();
    }
  }
}

export default ContactForm;
